import { Checkbox } from "@mui/material";
import type { MouseEvent } from "react";
import { useMemo } from "react";
import { v4 } from "uuid";

import { Item } from "@citrine/atoms/item";
import { isTest } from "@citrine/configuration";
import type { ColorName } from "@citrine/theme";
import { getArrayMap } from "@citrine/utils/helpers";

import * as sx from "./use-menu-items.sx";
import type { SelectOptionObject } from "./use-option-objects";

export const EMPTY_SELECT_OPTION_VALUE = isTest ? "Empty" : `Empty ${v4()}`;

type MultipleProps<Value extends string | undefined = string> =
  | {
      multiple: true;
      value?: Value[];
    }
  | {
      multiple?: false;
      value?: Value;
    };

export type ContentProps<Value extends string | undefined = string> = {
  color?: ColorName;
  options: SelectOptionObject<Value>[];
} & MultipleProps<Value>;

function preventDefault(e: MouseEvent) {
  e.preventDefault();
}

/**
 * originally I had written this as a custom Component. However, <Select/>
 * requires that <MenuItem/> be the direct descendant, so it doesn't work to
 * wrap it.
 */
const MenuItemContent = <ValueType extends string = string>({
  color,
  option,
  multiple,
  value,
}: {
  color?: ColorName;
  option: SelectOptionObject<ValueType>;
} & MultipleProps<ValueType>) => {
  return multiple && option.value !== EMPTY_SELECT_OPTION_VALUE ? (
    <sx.ChecklistFormControl
      label={<Item {...option} />}
      // preventDefault so that the entire MenuItem is clickable -
      // without this the `label` portion does not behave
      onClick={preventDefault}
      control={
        <Checkbox color={color} checked={value?.includes(option.value)} />
      }
    />
  ) : (
    <Item {...option} />
  );
};

function menuItemWithSeparator({
  color,
  option,
  multipleProps,
}: {
  color?: ColorName;
  option: SelectOptionObject;
  multipleProps: MultipleProps;
}) {
  return [
    <sx.MenuItem
      key={`item:${option.group}:${option.value}`}
      color={color}
      multiple={multipleProps.multiple}
      value={option.value}
      disabled={option.disabled}
      onClick={option.onClick}
    >
      <MenuItemContent color={color} option={option} {...multipleProps} />
    </sx.MenuItem>,
    option.addSeparator ? (
      <hr key={`hr:${option.group}:${option.value}`} />
    ) : null,
  ];
}

function getMenuItems<ValueType extends string = string>({
  color,
  options,
  ...multipleProps
}: ContentProps<ValueType>): React.ReactElement[] {
  // Groups options by group, putting options with no group under the key ""
  const optionMap = getArrayMap(options, (option) => option.group ?? "");

  return (optionMap[""] ?? [])
    .flatMap((option) =>
      menuItemWithSeparator({
        multipleProps,
        option,
        color: option.color ?? color,
      })
    )
    .concat(
      // Concat grouped options after ungrouped options
      Object.entries(optionMap)
        .filter(([group]) => group)
        .flatMap(([group, groupOptions]) => {
          return [
            <sx.ListSubheader key={`header:${group}`}>
              {group}
            </sx.ListSubheader>,
            ...groupOptions!.flatMap((option) =>
              menuItemWithSeparator({
                multipleProps,
                option,
                color: option.color ?? color,
              })
            ),
          ];
        })
    )
    .filter(Boolean);
}

export function useMenuItems<ValueType extends string = string>({
  color,
  multiple,
  options,
  value,
}: ContentProps<ValueType>) {
  return useMemo(
    () =>
      getMenuItems<ValueType>({
        color,
        multiple,
        options,
        value,
      } as ContentProps<ValueType>),
    [color, multiple, options, value]
  );
}
